import React, {useState} from "react";
import {
  Tag,
  Divider,
  Button,
  Skeleton,
  DatePicker,
  Tooltip,
  Table,
  Select,
  Grid,
} from "antd";
import {
  EyeOutlined,
  LinkOutlined,
  HourglassOutlined,
  LineChartOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
  TeamOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import {EmailPreview} from "./EmailPreview";
import type {EmailAutomationRow, EmailAutomationStatus} from "./types";
import {normalizeEmailAutomation} from "./types";
import {statusTag} from "./constants";
import {
  useGetEmailAutomationById,
  useGetEmailAutomationAnalytics,
  useGetEmailAutomationRecipients,
  useResendEmailAutomation,
  useSetEmailAutomationPaused,
  type EmailAutomationRecipient,
} from "../../../../../react-query-hooks/email-automation";
import {
  StyledEmailAutomationsTableWrapper,
  StyledViewAutomationDrawer,
} from "../../automations.style";

interface ViewAutomationDrawerProps {
  visible: boolean;
  onClose: () => void;
  automation: EmailAutomationRow | null;
  onEdit: () => void;
  onDelete: () => void;
}

function normalizeStatus(status: string): string {
  if (!status) return status;
  return status.charAt(0).toUpperCase() + status.slice(1).toLowerCase();
}

function getStatusStyle(status: string): {color: string; bg: string; border: string} {
  const normalized = normalizeStatus(status);
  switch (normalized) {
    case "Delivered":
      return {color: "#d4b106", bg: "#fffbe6", border: "1px solid #ffe58f"};
    case "Opened":
      return {color: "#52c41a", bg: "#f6ffed", border: "1px solid #b7eb8f"};
    case "Bounced":
      return {color: "#ff4d4f", bg: "#fff2f0", border: "1px solid #ffccc7"};
    case "Complained":
    case "Spammed":
      return {color: "#13c2c2", bg: "#e6fffb", border: "1px solid #87e8de"};
    case "Sent":
      return {color: "#722ed1", bg: "#f9f0ff", border: "1px solid #d3adf7"};
    case "Clicked":
      return {color: "#1890ff", bg: "#e6f7ff", border: "1px solid #91d5ff"};
    default:
      return {color: "#595959", bg: "#f5f5f5", border: "1px solid #d9d9d9"};
  }
}

function formatTimingFromApi(
  sendTime: "immediate" | "custom",
  delayTimeHours: number,
  trigger: "after-purchase" | "abandoned-purchase"
): string {
  if (trigger === "abandoned-purchase") {
    return "30 minutes after abandonment";
  }
  const context = "after purchase";
  if (sendTime === "immediate") return `Immediately ${context}`;
  if (delayTimeHours % 24 === 0 && delayTimeHours >= 24) {
    const days = delayTimeHours / 24;
    return `${days} ${days === 1 ? "day" : "days"} ${context}`;
  }
  if (delayTimeHours >= 1) {
    return `${delayTimeHours} ${delayTimeHours === 1 ? "hour" : "hours"} ${context}`;
  }
  const mins = Math.round(delayTimeHours * 60);
  return `${mins} ${mins === 1 ? "minute" : "minutes"} ${context}`;
}

export function ViewAutomationDrawer({
  visible,
  onClose,
  automation,
  onEdit,
  onDelete,
}: ViewAutomationDrawerProps) {
  const [recipientsPage, setRecipientsPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<string>("All");
  const [resendingEmail, setResendingEmail] = useState<string | null>(null);

  const detailQuery = useGetEmailAutomationById(automation?.key ?? null);
  const detail = detailQuery.data;
  const analyticsQuery = useGetEmailAutomationAnalytics(automation?.key ?? null);
  const analytics = analyticsQuery.data;

  const recipientsQuery = useGetEmailAutomationRecipients(
    automation?.key ?? null,
    recipientsPage,
    10,
    statusFilter === "All" ? null : statusFilter
  );
  const resendMutation = useResendEmailAutomation();
  const setPausedMutation = useSetEmailAutomationPaused();

  const screens = Grid.useBreakpoint();

  const isMdUp = Boolean(screens.md);
  /** 3-column metrics on desktop/tablet-wide; narrow screens stack for readability */
  const performanceGridTemplateColumns = isMdUp
    ? "repeat(3, minmax(0, 1fr))"
    : Boolean(screens.sm)
    ? "repeat(2, minmax(0, 1fr))"
    : "minmax(0, 1fr)";

  if (!automation) return null;

  const displayStatus: EmailAutomationStatus = detail
    ? normalizeEmailAutomation(detail).status
    : automation.status;

  const canPauseOrResume = displayStatus !== "Draft";
  const isPaused = displayStatus === "Paused";
  const pauseResumeLoading =
    setPausedMutation.isPending &&
    String(setPausedMutation.variables?.id) === automation.key;

  const handlePauseResume = () => {
    setPausedMutation.mutate({
      id: automation.key,
      isPaused: !isPaused,
    });
  };

  const formatPercentage = (decimal: number): string => {
    return `${(decimal * 100).toFixed(1)}%`;
  };

  const handleResend = (recipientEmail: string, recipientName: string) => {
    setResendingEmail(recipientEmail);
    resendMutation.mutate(
      {id: automation.key, recipientEmail, recipientName},
      {
        onSettled: () => setResendingEmail(null),
      }
    );
  };

  const recipientsData =
    recipientsQuery.data?.items.map((item, index) => ({
      key: `${item.email}-${index}`,
      ...item,
    })) ?? [];

  const recipientColumns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
      render: (text: string) => (
        <span style={{color: "#1c1726", fontWeight: 500}}>{text}</span>
      ),
    },
    {
      title: "Email address",
      dataIndex: "email",
      key: "email",
      render: (text: string) => (
        <span style={{color: "#1c1726", fontWeight: 500}}>{text}</span>
      ),
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status: string) => {
        const styles = getStatusStyle(status);
        return (
          <span
            style={{
              color: styles.color,
              background: styles.bg,
              border: styles.border,
              padding: "2px 8px",
              borderRadius: "4px",
              fontSize: "12px",
              fontWeight: 600,
            }}
          >
            {normalizeStatus(status)}
          </span>
        );
      },
    },
    // {
    //   title: "Date sent",
    //   dataIndex: "dateSent",
    //   key: "dateSent",
    //   render: (text: string) => (
    //     <span style={{color: "#595959", fontSize: "13px"}}>{formatDateSent(text)}</span>
    //   ),
    // },
    {
      title: "",
      key: "action",
      align: "right" as const,
      render: (_: unknown, record: EmailAutomationRecipient & {key: string}) => {
        const isResending = resendingEmail === record.email;
        return (
          <Button
            type="link"
            style={{
              color: "#1c1726",
              fontWeight: 600,
              padding: 0,
              fontSize: "14px",
            }}
            onClick={() => handleResend(record.email, record.name)}
            disabled={isResending}
            loading={isResending}
          >
            <span style={{textDecoration: "underline", textUnderlineOffset: "2px"}}>
              {isResending ? "Resending..." : "Resend"}
            </span>
          </Button>
        );
      },
    },
  ];

  return (
    <StyledViewAutomationDrawer
      title={
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "12px",
            minWidth: 0,
            width: "100%",
          }}
        >
          <span
            style={{
              fontSize: "20px",
              fontWeight: 700,
              color: "#1c1726",
              minWidth: 0,
              flex: 1,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {automation.name}
          </span>
          <CloseOutlined
            onClick={onClose}
            style={{color: "#1c1726", flexShrink: 0, cursor: "pointer"}}
          />
        </div>
      }
      placement="right"
      onClose={onClose}
      visible={visible}
      closable={false}
      bodyStyle={{
        padding: `24px 12px calc(48px + env(safe-area-inset-bottom, 0px))`,
        background: "#fcfcff",
      }}
      headerStyle={{
        borderBottom: "1px solid #f0f0f0",
        padding: isMdUp ? "24px" : "16px 12px",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
          marginBottom: "16px",
        }}
      >
        <span style={{fontSize: "16px", fontWeight: 700, color: "#1c1726"}}>Preview</span>
        <EyeOutlined style={{color: "#8c8c8c", fontSize: "16px"}} />
        <Tag
          color={statusTag[displayStatus].color}
          style={{
            margin: 0,
            borderRadius: "4px",
            fontWeight: 600,
            textTransform: "capitalize",
            ...(displayStatus === "Paused"
              ? {
                  background: "#f5f5f5",
                  border: "1px solid #d9d9d9",
                  color: "#595959",
                }
              : undefined),
          }}
        >
          {displayStatus}
        </Tag>
      </div>

      <div style={{marginBottom: "32px"}}>
        {detailQuery.isLoading ? (
          <Skeleton active paragraph={{rows: 6}} />
        ) : (
          <EmailPreview
            subject={detail?.subject.template ?? ""}
            body={detail?.body.template ?? ""}
            buttonText={detail?.buttonText ?? ""}
            addCustomButton={detail?.customButton ?? false}
          />
        )}
      </div>

      <Divider style={{margin: "0 0 24px 0"}} />

      <div style={{marginBottom: "32px"}}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginBottom: "16px",
          }}
        >
          <span style={{fontSize: "16px", fontWeight: 700, color: "#1c1726"}}>
            Linked offerings
          </span>
          <LinkOutlined style={{color: "#8c8c8c", fontSize: "16px"}} />
        </div>
        {detailQuery.isLoading ? (
          <Skeleton active paragraph={{rows: 2}} />
        ) : (
          <div style={{display: "flex", flexWrap: "wrap", gap: "12px"}}>
            {(detail?.settings?.pivot ?? []).map((pivot) => (
              <div
                key={pivot.id}
                style={{
                  background: "#f7f7f7",
                  padding: "6px 12px 6px 6px",
                  borderRadius: "50px",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <div
                  style={{
                    width: 24,
                    height: 24,
                    borderRadius: "50%",
                    background: "linear-gradient(135deg, #0052d4, #4364f7, #6fb1fc)",
                  }}
                />
                <span style={{fontSize: "14px", color: "#1c1726"}}>
                  {pivot.offeringName}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>

      <Divider style={{margin: "0 0 24px 0"}} />

      <div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            marginBottom: "16px",
          }}
        >
          <span style={{fontSize: "16px", fontWeight: 700, color: "#1c1726"}}>
            Scheduled timing
          </span>
          <HourglassOutlined style={{color: "#8c8c8c", fontSize: "16px"}} />
        </div>
        {detailQuery.isLoading ? (
          <Skeleton.Input active style={{width: 200}} />
        ) : (
          <div
            style={{
              background: "#edf4e8",
              padding: "6px 16px 6px 6px",
              borderRadius: "50px",
              display: "inline-flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <div
              style={{
                width: 24,
                height: 24,
                borderRadius: "50%",
                background: "#2b4c30",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <HourglassOutlined style={{color: "#fff", fontSize: "12px"}} />
            </div>
            <span style={{fontSize: "14px", color: "#2b4c30", fontWeight: 500}}>
              {detail
                ? formatTimingFromApi(
                    detail.settings?.sendTime ?? "immediate",
                    detail.settings?.delayTime ?? 0,
                    detail.trigger
                  )
                : "—"}
            </span>
          </div>
        )}
      </div>
      <Divider style={{margin: "24px 0"}} />

      {/* Performance Section */}
      <div>
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            flexWrap: "wrap",
            gap: "12px",
            marginBottom: "16px",
          }}
        >
          <div style={{display: "flex", alignItems: "center", gap: "8px"}}>
            <span style={{fontSize: "16px", fontWeight: 700, color: "#1c1726"}}>
              Performance
            </span>
            <LineChartOutlined style={{color: "#8c8c8c", fontSize: "16px"}} />
          </div>
          <DatePicker.RangePicker
            disabled
            style={{
              borderRadius: "8px",
              padding: "6px 12px",
              boxShadow: "none",
              borderColor: "#e0e0e0",
              visibility: "hidden",
            }}
          />
        </div>

        {analyticsQuery.isLoading ? (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: performanceGridTemplateColumns,
              gap: "16px",
            }}
          >
            {[...Array(6)].map((_, i) => (
              <div
                key={i}
                style={{
                  border: "1px solid #e0e0e0",
                  borderRadius: "12px",
                  padding: "16px",
                }}
              >
                <Skeleton active paragraph={{rows: 1}} />
              </div>
            ))}
          </div>
        ) : (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: performanceGridTemplateColumns,
              gap: "16px",
            }}
          >
            {/* Sent Card */}
            <div
              style={{
                border: "1px solid #e0e0e0",
                borderRadius: "12px",
                padding: "16px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "16px",
                }}
              >
                <span style={{fontSize: "12px", fontWeight: 600, color: "#595959"}}>
                  SENT
                </span>
                <Tooltip
                  title={
                    <div style={{fontSize: "12px", padding: "4px"}}>
                      Emails that were successfully sent from our system
                    </div>
                  }
                  color="#001529"
                  overlayInnerStyle={{borderRadius: "4px"}}
                >
                  <InfoCircleOutlined style={{color: "#8c8c8c", cursor: "pointer"}} />
                </Tooltip>
              </div>
              <div style={{fontSize: "20px", fontWeight: 700, color: "#1c1726"}}>
                {analytics?.sent ?? 0}
              </div>
            </div>

            {/* Delivered Card */}
            <div
              style={{
                border: "1px solid #e0e0e0",
                borderRadius: "12px",
                padding: "16px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "16px",
                }}
              >
                <span style={{fontSize: "12px", fontWeight: 600, color: "#595959"}}>
                  DELIVERED
                </span>
                <Tooltip
                  title={
                    <div style={{fontSize: "12px", padding: "4px"}}>
                      Emails that were accepted by the recipient's email provider
                    </div>
                  }
                  color="#001529"
                  overlayInnerStyle={{borderRadius: "4px"}}
                >
                  <InfoCircleOutlined style={{color: "#8c8c8c", cursor: "pointer"}} />
                </Tooltip>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: "8px",
                }}
              >
                <span style={{fontSize: "20px", fontWeight: 700, color: "#1c1726"}}>
                  {analytics?.delivered ?? 0}
                </span>
                <span
                  style={{
                    background: "#e6f7ff",
                    color: "#006d75",
                    padding: "2px 8px",
                    borderRadius: "12px",
                    fontSize: "12px",
                    fontWeight: 600,
                  }}
                >
                  {analytics?.deliveryRate != null
                    ? formatPercentage(analytics.deliveryRate)
                    : "0%"}
                </span>
              </div>
            </div>

            {/* Open Rate Card */}
            <div
              style={{
                border: "1px solid #e0e0e0",
                borderRadius: "12px",
                padding: "16px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "16px",
                }}
              >
                <span style={{fontSize: "12px", fontWeight: 600, color: "#595959"}}>
                  OPEN RATE
                </span>
                <Tooltip
                  title={
                    <div style={{fontSize: "12px", padding: "4px"}}>
                      Delivered emails that were opened
                    </div>
                  }
                  color="#001529"
                  overlayInnerStyle={{borderRadius: "4px"}}
                >
                  <InfoCircleOutlined style={{color: "#8c8c8c", cursor: "pointer"}} />
                </Tooltip>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: "8px",
                }}
              >
                <span style={{fontSize: "20px", fontWeight: 700, color: "#1c1726"}}>
                  {analytics?.opened ?? 0}
                </span>
                <span
                  style={{
                    background: "#e6f7ff",
                    color: "#006d75",
                    padding: "2px 8px",
                    borderRadius: "12px",
                    fontSize: "12px",
                    fontWeight: 600,
                  }}
                >
                  {analytics?.openRate != null
                    ? formatPercentage(analytics.openRate)
                    : "0%"}
                </span>
              </div>
            </div>

            {/* Click Rate Card */}
            <div
              style={{
                border: "1px solid #e0e0e0",
                borderRadius: "12px",
                padding: "16px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "16px",
                }}
              >
                <span style={{fontSize: "12px", fontWeight: 600, color: "#595959"}}>
                  CLICK RATE
                </span>
                <Tooltip
                  title={
                    <div style={{fontSize: "12px", padding: "4px"}}>
                      Emails where at least one link was clicked
                    </div>
                  }
                  color="#001529"
                  overlayInnerStyle={{borderRadius: "4px"}}
                >
                  <InfoCircleOutlined style={{color: "#8c8c8c", cursor: "pointer"}} />
                </Tooltip>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: "8px",
                }}
              >
                <span style={{fontSize: "20px", fontWeight: 700, color: "#1c1726"}}>
                  {analytics?.clicked ?? 0}
                </span>
                <span
                  style={{
                    background: "#e6f7ff",
                    color: "#006d75",
                    padding: "2px 8px",
                    borderRadius: "12px",
                    fontSize: "12px",
                    fontWeight: 600,
                  }}
                >
                  {analytics?.clickRate != null
                    ? formatPercentage(analytics.clickRate)
                    : "0%"}
                </span>
              </div>
            </div>

            {/* Bounce Rate Card */}
            <div
              style={{
                border: "1px solid #e0e0e0",
                borderRadius: "12px",
                padding: "16px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "16px",
                }}
              >
                <span style={{fontSize: "12px", fontWeight: 600, color: "#595959"}}>
                  BOUNCE RATE
                </span>
                <Tooltip
                  title={
                    <div style={{fontSize: "12px", padding: "4px"}}>
                      Emails that could not be delivered to the recipient. This may be due
                      to invalid or inactive email addresses
                    </div>
                  }
                  color="#001529"
                  overlayInnerStyle={{borderRadius: "4px"}}
                >
                  <InfoCircleOutlined style={{color: "#8c8c8c", cursor: "pointer"}} />
                </Tooltip>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: "8px",
                }}
              >
                <span style={{fontSize: "20px", fontWeight: 700, color: "#1c1726"}}>
                  {analytics?.bounced ?? 0}
                </span>
                <span
                  style={{
                    background: "#e6f7ff",
                    color: "#006d75",
                    padding: "2px 8px",
                    borderRadius: "12px",
                    fontSize: "12px",
                    fontWeight: 600,
                  }}
                >
                  {analytics?.bounceRate != null
                    ? formatPercentage(analytics.bounceRate)
                    : "0%"}
                </span>
              </div>
            </div>

            {/* Spam Rate Card */}
            <div
              style={{
                border: "1px solid #e0e0e0",
                borderRadius: "12px",
                padding: "16px",
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  marginBottom: "16px",
                }}
              >
                <span style={{fontSize: "12px", fontWeight: 600, color: "#595959"}}>
                  SPAM RATE
                </span>
                <Tooltip
                  title={
                    <div style={{fontSize: "12px", padding: "4px"}}>
                      Emails that are marked as spam by the recipient or email provider
                    </div>
                  }
                  color="#001529"
                  overlayInnerStyle={{borderRadius: "4px"}}
                >
                  <InfoCircleOutlined style={{color: "#8c8c8c", cursor: "pointer"}} />
                </Tooltip>
              </div>
              <div
                style={{
                  display: "flex",
                  alignItems: "baseline",
                  gap: "8px",
                }}
              >
                <span style={{fontSize: "20px", fontWeight: 700, color: "#1c1726"}}>
                  {analytics?.complained ?? 0}
                </span>
                <span
                  style={{
                    background: "#e6f7ff",
                    color: "#006d75",
                    padding: "2px 8px",
                    borderRadius: "12px",
                    fontSize: "12px",
                    fontWeight: 600,
                  }}
                >
                  {analytics?.complaintRate != null
                    ? formatPercentage(analytics.complaintRate)
                    : "0%"}
                </span>
              </div>
            </div>
          </div>
        )}
      </div>

      <Divider style={{margin: "40px 0 24px"}} />

      {/* Recipients' Activity Section */}
      <div style={{marginBottom: "40px"}}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexWrap: "wrap",
            gap: "12px",
            marginBottom: "24px",
          }}
        >
          <div style={{display: "flex", alignItems: "center", gap: "8px"}}>
            <span style={{fontSize: "20px", fontWeight: 700, color: "#1c1726"}}>
              Recipients&apos; activity
            </span>
            <TeamOutlined style={{fontSize: "18px", color: "#1c1726"}} />
          </div>
          <Select
            value={statusFilter}
            style={{width: isMdUp ? 120 : "100%", minWidth: isMdUp ? 120 : 0}}
            onChange={(value) => {
              setStatusFilter(value);
              setRecipientsPage(1);
            }}
            options={[
              {value: "All", label: "All"},
              {value: "sent", label: "Sent"},
              {value: "delivered", label: "Delivered"},
              {value: "opened", label: "Opened"},
              // {value: "clicked", label: "Clicked"},
              {value: "bounced", label: "Bounced"},
              {value: "complained", label: "Spammed"},
            ]}
          />
        </div>

        <StyledEmailAutomationsTableWrapper
          style={{marginTop: 0, marginBottom: "24px", overflowX: "auto"}}
        >
          <Table
            columns={recipientColumns}
            dataSource={recipientsData}
            loading={recipientsQuery.isLoading || recipientsQuery.isFetching}
            scroll={{x: 600}}
            style={{
              border: "1px solid #f0f0f0",
              borderRadius: "8px",
              overflow: "hidden",
            }}
            pagination={{
              position: ["bottomLeft"],
              current: recipientsPage,
              pageSize: 10,
              total: recipientsQuery.data?.total ?? 0,
              showSizeChanger: false,
              onChange: (page) => {
                setRecipientsPage(page);
              },
              itemRender: (page, type, originalElement) => {
                if (type === "prev") return <Button size="small">Previous</Button>;
                if (type === "next") return <Button size="small">Next</Button>;
                if (type === "page") {
                  const isActive = page === recipientsPage;
                  return (
                    <Button
                      size="small"
                      style={isActive ? {backgroundColor: "#eae6f3"} : {}}
                    >
                      {page}
                    </Button>
                  );
                }
                return originalElement;
              },
            }}
          />
        </StyledEmailAutomationsTableWrapper>
      </div>
      <div
        style={{
          position: "absolute",
          bottom: 0,
          left: 0,
          width: "100%",
          paddingTop: isMdUp ? 16 : 12,
          paddingBottom: isMdUp ? 16 : `calc(12px + env(safe-area-inset-bottom, 0px))`,
          paddingLeft: isMdUp ? 24 : 12,
          paddingRight: isMdUp ? 24 : 12,
          borderTop: "1px solid #f0f0f0",
          background: "#f5f5f5",
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "12px",
          boxSizing: "border-box",
        }}
      >
        <Button
          type="primary"
          style={{
            background: "#1c1726",
            borderRadius: "8px",
            height: "44px",
            padding: "0 24px",
            fontWeight: 600,
            ...(isMdUp ? {} : {flex: "1 1 auto", minWidth: "140px"}),
          }}
          onClick={onEdit}
        >
          Edit
        </Button>
        {canPauseOrResume ? (
          <Button
            type="default"
            style={{
              borderRadius: "8px",
              height: "44px",
              padding: "0 24px",
              fontWeight: 600,
              borderColor: "#1c1726",
              color: "#1c1726",
              background: "transparent",
              ...(isMdUp ? {} : {flex: "1 1 auto", minWidth: "140px"}),
            }}
            onClick={handlePauseResume}
            loading={pauseResumeLoading}
            disabled={pauseResumeLoading}
          >
            {isPaused ? "Resume" : "Pause"}
          </Button>
        ) : null}
        <Button
          type="text"
          icon={<DeleteOutlined />}
          style={{
            color: "#ff4d4f",
            height: "44px",
            width: "44px",
            marginLeft: "auto",
          }}
          onClick={onDelete}
        />
      </div>
    </StyledViewAutomationDrawer>
  );
}
